/**
 * Shared navigation section data used by both NavPanel (desktop) and MobileDrawer.
 */
import {
  Activity,
  AlertTriangle,
  BarChart3,
  BookOpen,
  Boxes,
  Briefcase,
  Bug,
  Building2,
  Calendar,
  CheckSquare,
  ClipboardList,
  Contact,
  DollarSign,
  FileText,
  Filter,
  Fingerprint,
  Flag,
  FlaskConical,
  FolderKanban,
  Gauge,
  GitBranch,
  GitCompare,
  GitPullRequest,
  HardDrive,
  HelpCircle,
  History,
  Kanban,
  KeyRound,
  Inbox,
  LayoutDashboard,
  LayoutTemplate,
  Mail,
  Map as MapIcon,
  MessagesSquare,
  Package,
  PenLine,
  PlayCircle,
  ScrollText,
  Settings,
  Share2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Target,
  Ticket,
  UserCircle,
  Users,
  Wallet,
  Wrench,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { isHiddenSurface } from "@/lib/projectSurfaces";

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  badgeKey?: string;
  badgeColor?: string;
  adminOnly?: boolean;
  platformAdminOnly?: boolean;
  /** Phase 7 (UX v2): hide this item unless the named v2 flag is on
   *  (e.g. "my_day", "portfolios"). When undefined, item shows for
   *  everyone (subject to admin checks above). NavPanel evaluates
   *  the flag via useV2Flags(). */
  v2Flag?: string;
}

export interface NavSection {
  title: string;
  items: NavItem[];
  adminOnly?: boolean;
  platformAdminOnly?: boolean;
}

export const globalSections: NavSection[] = [
  {
    title: "MAIN",
    items: [
      // T3 one-home collapse (2026-07-11, Claire finding 1): "/" is the ONE
      // home — the old "Dashboard" label made it read as a second landing.
      // This legacy-sections copy is filtered out of the rail by
      // PRIMARY_PATHS below; legacy consumers (NavPanel/Sidebar/
      // ActivityRibbon) now show the same word as the primary rail.
      { label: "Home", path: "/", icon: LayoutDashboard },
      // My Day / My Week consolidated into the Calendar hub (2026-06-06, Bob):
      // the calendar's day/week views + narrative toggle ARE my-day/my-week.
      // Routes remain (deep-linkable); the nav entries retire to avoid
      // duplicating the calendar. See primaryGlobalItems "Calendar".
      { label: "Projects", path: "/projects", icon: FolderKanban },
      // Marketplace (template gallery) is intentionally NOT a nav entry — it's
      // reached from the Projects "New Project ▸ From a template" flow + a
      // "Browse templates" link, where its only job (instantiate → new
      // project) belongs. Route /marketplace stays deep-linkable. Bob
      // 2026-06-08: it doesn't earn a primary slot; it's a project-creation
      // entry point, not a daily-driver.
      // Portfolios + Programs are subsumed by the primary "Portfolio" hub
      // (/portfolio — the Portfolio → Program → Project constellation). The
      // old flat /portfolios + /programs CRUD pages stay deep-linkable but
      // leave the nav (Bob 2026-06-08).
      { label: "Team", path: "/team", icon: Users },
      // Staffing-intelligence surfaces (2026-07-19): the /skills catalog +
      // workload-balance hub and the /staffing-simulation what-if tool shipped
      // their pages + routes (App.tsx) but had no rail entry — reachable by URL
      // only (infra-but-not-connected). Wired here so the whole staffing UX is
      // discoverable; both demote into the FelixRail "More ▸ Org" overflow.
      // Backend endpoints enforce their own authz on the reassignment mutations.
      { label: "Skills & Staffing", path: "/skills", icon: Wrench },
      { label: "Staffing Simulation", path: "/staffing-simulation", icon: Gauge },
      { label: "My Profile", path: "/profile", icon: UserCircle },
      { label: "Billing", path: "/billing", icon: DollarSign },
      { label: "Conversations", path: "/conversations", icon: MessagesSquare },
      { label: "Action Items", path: "/action-items", icon: CheckSquare },
      { label: "Documents", path: "/documents", icon: BookOpen },
      // Phase 5 (UX v2) org-level briefings — flag-gated by master.
      // Nav-consolidation PR-1 (2026-07-11): repointed from /risks /decisions
      // /changes — those routes never existed; the REAL App.tsx routes are the
      // long-form slugs below (verified against App.tsx:347-349).
      { label: "Risks & Blockers", path: "/risks-and-blockers", icon: ShieldAlert, v2Flag: "master" },
      { label: "Decisions Needed", path: "/decisions-needed", icon: GitPullRequest, v2Flag: "master" },
      { label: "Change Summary", path: "/change-summary", icon: History, v2Flag: "master" },
      { label: "Reports", path: "/reports", icon: BarChart3 },
      { label: "Release Notes", path: "/release-notes", icon: FileText },
    ],
  },
  {
    title: "WORKSPACE",
    items: [
      // "Hub" → "Workspace" (T3 one-home collapse): /workspace is a
      // data-dense workspace view, not a landing — "Hub" read as a rival
      // home (Claire finding 1: Home ≡ Workspace Hub duplication).
      { label: "Workspace", path: "/workspace", icon: LayoutDashboard },
      { label: "Gmail", path: "/gmail", icon: Mail },
      { label: "Calendar", path: "/calendar", icon: Calendar },
      { label: "Contacts", path: "/contacts", icon: Contact },
      { label: "Drive", path: "/drive", icon: HardDrive },
    ],
  },
  {
    // Broadcast (marketing automation) shipped seven full surfaces — leads,
    // segments, templates, analytics, content, approvals, social — and every
    // one of them was reachable ONLY by typing the URL: no nav entry, no
    // command-palette entry, nothing. ~1,500 LOC of a paid add-on that a
    // customer could not find.
    //
    // Deliberately a new SECTION rather than an entry in primaryGlobalItems:
    // the primary rail is a fixed four-slot affordance (FelixRail asserts
    // exactly four links) and these are not daily-drivers. A section flows
    // automatically into overflowGlobalSections and renders collapsed.
    //
    // Deliberately NOT gated on a feature flag: NavItem supports only `v2Flag`,
    // and the Broadcast v3 flag defaults OFF — gating here would leave the
    // pages exactly as invisible as they were, which is the bug. The
    // /marketing/* routes themselves are ungated, so nav matches routing.
    title: "BROADCAST",
    items: [
      { label: "Leads", path: "/marketing/leads", icon: Contact },
      { label: "Segments", path: "/marketing/segments", icon: Filter },
      { label: "Templates", path: "/marketing/templates", icon: LayoutTemplate },
      { label: "Content", path: "/marketing/content", icon: PenLine },
      { label: "Social", path: "/marketing/social", icon: Share2 },
      { label: "Approvals", path: "/marketing/approvals", icon: CheckSquare },
      { label: "Campaign analytics", path: "/marketing/analytics", icon: BarChart3 },
    ],
  },
  {
    title: "ADMIN",
    adminOnly: true,
    items: [
      { label: "Users", path: "/admin/users", icon: Users, adminOnly: true },
      // Row f9452535: FTL's domain resale margin is FTL staff's, never a client org's.
      { label: "Domain Pricing", path: "/admin/domain-pricing", icon: DollarSign, platformAdminOnly: true },
      { label: "Organization Settings", path: "/organization/settings", icon: Settings, adminOnly: true },
      { label: "Felix Controls", path: "/organization/felix-controls", icon: Gauge, adminOnly: true },
      { label: "Roles", path: "/roles", icon: Shield, adminOnly: true },
      { label: "Access & Subscription", path: "/admin/access", icon: KeyRound, adminOnly: true },
      { label: "Audit Log", path: "/audit-log", icon: ScrollText, adminOnly: true },
      // Compliance/DSAR console. The six /compliance endpoints shipped with no
      // frontend consumer at all, so orgs paying for COMPLIANCE_OPS could not run
      // a data-subject request. The page itself explains the plan gate on a 403.
      { label: "Compliance", path: "/compliance", icon: ShieldCheck, adminOnly: true },
    ],
  },
  {
    title: "PLATFORM",
    platformAdminOnly: true,
    items: [
      { label: "Command Center", path: "/platform", icon: Zap, platformAdminOnly: true },
      { label: "Organizations", path: "/platform/organizations", icon: Building2, platformAdminOnly: true },
      { label: "Users", path: "/platform/users", icon: Users, platformAdminOnly: true },
      { label: "Access Requests", path: "/platform/access-requests", icon: KeyRound, platformAdminOnly: true },
      // Row 83b07d62: the key list/revoke and support-message routes had no page.
      { label: "API Keys", path: "/platform/api-keys", icon: KeyRound, platformAdminOnly: true },
      { label: "Support Inbox", path: "/platform/support", icon: Inbox, platformAdminOnly: true },
      { label: "Trial Codes", path: "/platform/trial-codes", icon: Ticket, platformAdminOnly: true },
      { label: "Configuration", path: "/platform/configuration", icon: SlidersHorizontal, platformAdminOnly: true },
      // Row 96ab5bdd: release a feature (e.g. Broadcast) to every org, or one.
      { label: "Feature Flags", path: "/platform/feature-flags", icon: Flag, platformAdminOnly: true },
      { label: "Felix Ops", path: "/platform/felix-ops", icon: Activity, platformAdminOnly: true },
      // The console that edits which statuses a record may move between. Its
      // page existed with no route at all; its write endpoints are
      // require_platform_admin, so it belongs in this block.
      { label: "Status Registry", path: "/platform/status-registry", icon: GitBranch, platformAdminOnly: true },
      { label: "Pricing", path: "/platform/pricing", icon: DollarSign, platformAdminOnly: true },
      { label: "Test Harness", path: "/platform/felix-quality", icon: Sparkles, platformAdminOnly: true },
      { label: "Felix Steering", path: "/platform/felix-steering", icon: Gauge, platformAdminOnly: true },
      { label: "Friction Findings", path: "/platform/friction-findings", icon: Sparkles, platformAdminOnly: true },
      { label: "Judge Calibration", path: "/platform/judge-calibration", icon: ShieldCheck, platformAdminOnly: true },
      { label: "UAT Pack", path: "/platform/uat-pack", icon: PlayCircle, platformAdminOnly: true },
      { label: "Supervisor", path: "/platform/supervisor", icon: Activity, platformAdminOnly: true },
      { label: "Feedback", path: "/platform/feedback", icon: Bug, platformAdminOnly: true },
      { label: "NDA Management", path: "/platform/nda", icon: Fingerprint, platformAdminOnly: true },
    ],
  },
];

// ---------------------------------------------------------------------------
// Felix-First Minimal Nav (2026-06-06) — partition of the legacy
// `globalSections` into a curated PRIMARY set + an OVERFLOW set.
//
// `globalSections` is retained verbatim above (NavPanel / ActivityRibbon /
// ReleaseNotes test still consume it). These two derived exports drive the
// new FelixRail: 5 always-visible destinations + everything else demoted
// into a single "More" disclosure. No route is removed — every path here
// still resolves; the rail's surface area is what shrinks.
// ---------------------------------------------------------------------------

/**
 * The 5 always-visible primary destinations, in rail order. Felix is the
 * protagonist (rendered separately as the hero block), so these are the
 * curated few "objects of the platform" a user reaches for daily.
 */
export const primaryGlobalItems: NavItem[] = [
  { label: "Home", path: "/", icon: LayoutDashboard },
  // Portfolio is the top of the hierarchy + the entry to all project work
  // (Bob 2026-06-08): /portfolio is the Portfolio → Program → Project
  // constellation hub; programs + projects are reached through it. The flat
  // /projects list stays reachable in the Org overflow group + deep-links.
  { label: "Portfolio", path: "/portfolio", icon: Briefcase },
  // Communications is the one-stop personal hub (Bob 2026-06-07: relabel the
  // calendar nav to "Communications" and run email/contacts through it). The
  // route stays /calendar (the Calendar page IS the comms hub: schedule +
  // comms lane + contacts). It SUBSUMES Action Items — every
  // assignment/deadline surfaces here as a deadline/comms event — so it takes
  // the primary slot (and the open-action-item badge, the daily "what's on my
  // plate" signal). Action Items demotes to the "More" overflow. The Google
  // Workspace "Calendar" link (WORKSPACE group) keeps its literal name.
  { label: "Communications", path: "/calendar", icon: Calendar, badgeKey: "actionItems" },
  { label: "Documents", path: "/documents", icon: BookOpen },
  // Marketplace removed from primary nav (Bob 2026-06-08) — it lives in the
  // Projects "New Project ▸ From a template" flow; see globalSections note.
];

/** Paths that graduate to PRIMARY — excluded from the overflow MAIN group. */
const PRIMARY_PATHS = new Set(primaryGlobalItems.map((i) => i.path));

/**
 * Paths dropped from the rail entirely (reachable via Felix + ⌘K only).
 * These are flag-gated briefing pages or duplicates of the Topbar UserMenu.
 * They stay routable; they just leave the rail to keep it minimal.
 */
const DROPPED_PATHS = new Set<string>([
  "/my-day",
  "/my-week",
  "/portfolios",
  "/programs",
  // Org briefing pages (renamed with their MAIN entries above — must match
  // exactly or the flag-gated rows leak back into the rail's Org overflow).
  "/risks-and-blockers",
  "/decisions-needed",
  "/change-summary",
  "/profile",
]);

/**
 * Everything that is neither primary nor dropped, grouped under the SAME
 * section titles as today so nothing is lost — surfaced by the FelixRail
 * "More" disclosure. The MAIN section is relabeled "Org" (its survivors are
 * org-level surfaces); WORKSPACE / ADMIN / PLATFORM carry through unchanged.
 */
export const overflowGlobalSections: NavSection[] = globalSections
  .map((section) => {
    // The MAIN section is relabeled "Org"; additionally drop the flag-gated
    // briefing duplicates listed in DROPPED_PATHS.
    if (section.title === "MAIN") {
      return {
        ...section,
        title: "Org",
        items: section.items.filter(
          (item) =>
            !PRIMARY_PATHS.has(item.path) && !DROPPED_PATHS.has(item.path),
        ),
      };
    }
    // Every other section keeps its title, but any item that has graduated
    // to PRIMARY is removed so it isn't listed twice (e.g. Calendar now
    // lives in the primary rail AND used to sit under WORKSPACE).
    return {
      ...section,
      items: section.items.filter((item) => !PRIMARY_PATHS.has(item.path)),
    };
  })
  .filter((section) => section.items.length > 0);

/**
 * Optional label resolver — the FTL template-layer SKIN. When a caller passes
 * the project's `label(canonicalType, { plural, fallback })` (from
 * useTemplateLabels), the entity-type nav rows relabel to the project's
 * manifest vocabulary (a marketing_campaign project reads "Campaign Goals",
 * "Channels", "Audience Segments" …). Every non-entity row (Plan Builder, Q&A
 * Sessions, Sprints, Conflicts, Baselines, Reports …) is a workflow/tool label,
 * NOT a canonical entity type, so it is left verbatim.
 *
 * ZERO-DRIFT: each skinnable row passes its EXACT current word as `fallback`,
 * so a project with no manifest renders byte-identical to today; the resolver
 * only diverges when a manifest relabel exists.
 */
export type NavLabelResolver = (
  canonicalType: string,
  opts?: { plural?: boolean; fallback?: string },
) => string;

// Identity resolver: when no skin is supplied, every entity row keeps its
// literal current label (used by any caller that hasn't threaded the hook yet).
const IDENTITY_RESOLVER: NavLabelResolver = (_type, opts) =>
  opts?.fallback ?? "";

/**
 * The VERIFY group — the ONE list of Verify pages (row c0c4dc39).
 *
 * The project navigation and the Verify shelf (shelves/VerifyShelf.tsx) both
 * render THIS list. They used to be two hand-written lists that disagreed: the
 * shelf offered Tests, Defects and "Quality" while the navigation put Tests, QA
 * Agent, Conflicts and Traceability under VERIFY, so a tester sent to "the
 * Verify shelf" could not find the QA Agent there at all. One function, two
 * readers, so they cannot drift again.
 *
 * ``base`` is the project prefix (`/projects/<id>`); ``label`` is the same
 * template-layer skin getProjectSections takes.
 */
export function getVerifyItems(
  base: string,
  label: NavLabelResolver = IDENTITY_RESOLVER,
): NavItem[] {
  return [
    { label: label("test_case", { plural: true, fallback: "Tests" }), path: `${base}/tests`, icon: FlaskConical },
    { label: "QA Agent", path: `${base}/qa-agent`, icon: PlayCircle },
    { label: "Conflicts", path: `${base}/conflicts`, icon: AlertTriangle, badgeKey: "conflicts", badgeColor: "bg-red-500" },
    { label: "Traceability", path: `${base}/traceability`, icon: GitBranch },
  ];
}

export function getProjectSections(
  projectId: string,
  label: NavLabelResolver = IDENTITY_RESOLVER,
  // Row 4aa3e0ec: route slugs this project type does not use (from the
  // template manifest via useTemplateLabels). A finance project drops Develop,
  // Iterator and QA Agent here; a software project passes nothing.
  hiddenSurfaces: readonly string[] = [],
): NavSection[] {
  return projectSectionsAll(projectId, label)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !isHiddenSurface(item.path, hiddenSurfaces)),
    }))
    .filter((section) => section.items.length > 0);
}

function projectSectionsAll(
  projectId: string,
  label: NavLabelResolver,
): NavSection[] {
  const base = `/projects/${projectId}`;
  // Skin an entity-type row: the manifest label wins; otherwise `current`
  // (the row's verbatim current word) is returned unchanged.
  const skin = (canonicalType: string, current: string): string =>
    label(canonicalType, { plural: true, fallback: current });
  return [
    {
      title: "PLAN",
      items: [
        { label: "Plan Builder", path: `${base}/plan`, icon: MapIcon },
        { label: skin("objective", "Objectives"), path: `${base}/objectives`, icon: Target },
        { label: skin("user_story", "User Stories"), path: `${base}/user-stories`, icon: BookOpen },
        { label: skin("user_persona", "Personas"), path: `${base}/personas`, icon: Users },
        { label: "Q&A Sessions", path: `${base}/qa-sessions`, icon: HelpCircle },
        { label: "Action Items", path: `${base}/action-items`, icon: CheckSquare },
        { label: "Knowledge Base", path: `${base}/knowledge-base`, icon: FileText },
      ],
    },
    {
      title: "DESIGN",
      items: [
        { label: skin("module", "Modules"), path: `${base}/modules`, icon: Package },
        { label: skin("component", "Components"), path: `${base}/components`, icon: Boxes, badgeKey: "components", badgeColor: "bg-emerald-500" },
        { label: "Specifications", path: `${base}/specifications`, icon: FileText },
        { label: skin("impl_task", "Tasks"), path: `${base}/tasks`, icon: CheckSquare, badgeKey: "tasks" },
      ],
    },
    {
      title: "EXECUTE",
      items: [
        // Develop Iterator — the BUILD half of the Iterator split: which agents
        // are working, what is blocked on you, and the rank-ordered backlog the
        // agents read. Listed FIRST in EXECUTE because it is the answer to
        // "what is happening right now", which is the question that sent a
        // founder to ask an agent directly on 2026-08-15.
        //
        // It shipped as a route with no nav entry, which made it reachable only
        // by typing the URL -- the same built-but-not-connected failure this
        // surface exists to expose.
        { label: "Develop", path: `${base}/develop`, icon: Kanban },
        // Iterator -- the CONVERSE half of the Iterator split: the
        // conversational build surface, where Develop is the board view of the
        // same work. Same failure as Develop had, measured 2026-08-22:
        // /projects/:projectId/iterator has been routed and ftl.ux.v3.iterator_mvp
        // is held by 20 of 100 orgs; 8 of the 8 orgs with a real login have it, and it appeared in NO nav entry -- so it
        // was reachable only by typing the URL.
        //
        // Project-scoped, not global, because the route REQUIRES a projectId. A
        // global "Iterator" entry would have no valid target; that is why this
        // sits beside Develop rather than in MAIN.
        { label: "Iterator", path: `${base}/iterator`, icon: MessagesSquare },
        { label: "Sprints", path: `${base}/sprints`, icon: Kanban },
        { label: skin("defect", "Defects"), path: `${base}/defects`, icon: Bug, badgeKey: "defects", badgeColor: "bg-red-500" },
      ],
    },
    { title: "VERIFY", items: getVerifyItems(base, label) },
    {
      title: "GOVERN",
      items: [
        { label: "Baselines", path: `${base}/baselines`, icon: GitCompare },
        { label: "Change Control", path: `${base}/change-control`, icon: Shield },
        { label: skin("document", "Documents"), path: `${base}/documents`, icon: FileText },
        { label: "Reports", path: `${base}/reports`, icon: BarChart3 },
        // Meetings + Meeting History consolidated into the Calendar hub
        // (2026-06-06, Bob): the calendar plots meetings (upcoming + past =
        // history) from /calendar/timeline and routes each to the room;
        // Felix surfaces history via the narrative toggle. Routes remain
        // deep-linkable; the nav entries retire to avoid duplication.
        { label: "Milestones", path: `${base}/milestones`, icon: Flag },
        // Budget: the contract price, payment milestones and (internal roles)
        // cost / reserve / margin, plus the EVM plan. Routed since #2845 but
        // linked only from the legacy ProjectOverview grid, which the Command
        // Center and a remembered mode both bypass.
        { label: "Budget", path: `${base}/budget`, icon: Wallet },
      ],
    },
    // Phase 5/7 (UX v2): project-scoped briefing pages. Each gated by
    // its flag so legacy users don't see entries that 404 against
    // their disabled flags.
    {
      title: "BRIEFINGS",
      items: [
        // Nav-consolidation PR-1 (2026-07-11): the last four rows were
        // repointed from short-form slugs that never existed as routes. The
        // REAL project-scoped routes are the long-form slugs below (verified
        // against App.tsx:370-373).
        { label: "Project Health", path: `${base}/health`, icon: Activity, v2Flag: "project_health" },
        { label: "Sponsor Update", path: `${base}/sponsor-update`, icon: BarChart3, v2Flag: "sponsor_update" },
        { label: "Delivery Update", path: `${base}/delivery-team-update`, icon: PlayCircle, v2Flag: "project_health" },
        { label: "Risks & Blockers", path: `${base}/risks-and-blockers`, icon: AlertTriangle, v2Flag: "master" },
        { label: "Decisions Needed", path: `${base}/decisions-needed`, icon: ClipboardList, v2Flag: "master" },
        { label: "Change Summary", path: `${base}/change-summary`, icon: GitBranch, v2Flag: "master" },
      ],
    },
  ];
}
